import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaEraser, FaFileCirclePlus, FaFileExport, FaMagnifyingGlass, FaUserPlus } from "react-icons/fa6";
import Swal from "sweetalert2";
import "./customer-list.style.scss";
import "../../components/form-inputs/form-inputs.style.scss";
import "../../components/bill-list/bill-filter.style.scss";

// for filter form handle
import { useFormik } from "formik";
import * as yup from "yup";

// components
import TextField from "../../components/form-inputs/text-field.component";
import SelectBox from "../../components/form-inputs/select-box.component";
import UserListItem from "../../components/user-list-item/user-list-item.component";
import CustomerProfile from "../../components/user-profile-sidemodal/customer-profile.component";
import EmptyRecord from "../../components/empty-record/empty-record.component";
import CustomerBulkDraft from "../../components/customer-bulk-draft/customer-bulk-draft.component";
import MultipleTagsInput from "../../components/form-inputs/multiple-tags-input.component";
import Spinner from "../../components/spinner/spinner.component";
import FormError from "../../components/form-error/form-error.component";
import InlineNotice from "../../components/inline-notice/inline-notice.component";

// global functions and strings
import strings from "../../global/strings";
// import { isEmptyObject } from "../../global/function";

// queries
import { useCustomers, Customer, CustomerFilters } from "../../queries/customer.queries";

import { useExportFile } from "../../queries/export.queries";
import { useSession } from "../../queries/auth.queries";
import {
	formatCustomerIdParam,
	CUSTOMER_ID_VALIDATION_PATTERN,
	CUSTOMER_ID_ERROR_MESSAGE,
} from "../../global/customer-id.util";

const customerSchema = yup.object().shape({
	customer_id: yup
		.string()
		.nullable()
		.matches(CUSTOMER_ID_VALIDATION_PATTERN, {
			message: CUSTOMER_ID_ERROR_MESSAGE,
			excludeEmptyString: true,
		}),
	customer_name: yup.string().nullable(),
	customer_mobile: yup.string().nullable(),
	customer_type: yup.string().nullable(),
	customer_email: yup.string().email("Please enter valid email").nullable(),
	customer_pan_vat: yup.string().nullable(),
});

const initial_values = {
	customer_id: "",
	company_customer_id: "",
	customer_pan_vat: "",
	customer_name: "",
	customer_email: "",
	customer_mobile: "",
	customer_gender: "",
	customer_type: "",
	customer_city: "",
	customer_address: "",
	customer_tag: [] as string[],
};

function CustomerList() {
	const { data: session } = useSession();
	const exportMutation = useExportFile();

	const [addCustomerView, setAddCustomerView] = useState(false);
	const [draftCreate, setDraftCreate] = useState(false);
	// Set when "Complete Profile" is picked on the add-customer success screen
	// — opens a second CustomerProfile instance straight into edit mode for
	// the record just created (see startEditable on customer-profile.component).
	const [completeProfileCustomer, setCompleteProfileCustomer] =
		useState<Partial<Customer> | null>(null);

	// Filters actually applied to the query — only updated on Search submit / Reset,
	// so typing in the filter form doesn't refetch on every keystroke.
	const [appliedFilters, setAppliedFilters] = useState<CustomerFilters>({});

	const {
		data,
		error,
		isError,
		isPending,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useCustomers(appliedFilters);

	const customers = useMemo(
		() =>
			data?.pages.flatMap((page, pageIndex) =>
				page.rows.map((row) => ({ row, page: pageIndex + 1 }))
			) ?? [],
		[data]
	);

	// Infinite scroll: fetch the next page once the sentinel enters the viewport
	const sentinelRef = useRef(null);

	useEffect(() => {
		const node = sentinelRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{ rootMargin: "200px" }
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	function convertTagToString(array: string[]): string {
		const string = array.join(".");
		return string;
	}

	const filterFormik = useFormik({
		validationSchema: customerSchema,
		initialValues: initial_values,
		onSubmit: async () => {
			// clientFilter (below) already strips empty fields and applies the
			// CUS_ prefix / tag-joining transform — reuse it as the applied filter set.
			setAppliedFilters(clientFilter);
		},
		onReset: async () => {
			setAppliedFilters({});
		},
	});

	const clientFilter = useMemo(() => {
		let filterObject: Record<string, unknown> = {
			...filterFormik.values,
			customer_id: formatCustomerIdParam(filterFormik.values?.customer_id),
			customer_tag:
				Array.isArray(filterFormik.values?.customer_tag) &&
				filterFormik.values?.customer_tag.length > 0
					? convertTagToString(filterFormik.values?.customer_tag)
					: "",
		};

		// Remove empty string properties
		for (const key in filterObject) {
			if (filterObject[key] === "") {
				delete filterObject[key];
			}
		}

		// Handle total_sales_min and total_sales_max condition
		if (filterObject.total_sales_max === 0) {
			delete filterObject.total_sales_min;
			delete filterObject.total_sales_max;
		}
		return { ...filterObject };
	}, [filterFormik.values]);

	function handleTagAdd(e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) {
		const target = e.target as HTMLInputElement;
		// Get the value of the input
		const value = target.value;
		// If the value is empty, return
		if (!value.trim()) return;
		// cleaning up all hash values
		const newValue = value?.split("#").filter(Boolean);

		// Convert the arrays to Sets to remove duplicates
		const set1 = new Set(filterFormik.values?.customer_tag);
		const set2 = new Set(newValue);

		// Merge the Sets
		const mergedSet = new Set([...set1, ...set2]);

		// Convert the merged Set back to an array
		const resultArray = Array.from(mergedSet);

		// Add the value to the tags array
		filterFormik.setFieldValue("customer_tag", resultArray);
		// Clear the input
		target.value = "";
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		// If user did not press enter key, return
		if (e.key !== "Enter") return;
		handleTagAdd(e);
	}

	function handleTagRemove(index: number) {
		let tags = [...filterFormik.values?.customer_tag];
		filterFormik.setFieldValue(
			"customer_tag",
			tags.filter((el, i) => i !== index)
		);
	}

	const handleClientExport = async () => {
		Swal.fire({
			title: "Export Customers Data",
			text: "All your filtered customer's list will be exported to excel file. Are you sure?",
			icon: "info",
			showDenyButton: true,
			confirmButtonText: "Export",
			denyButtonText: "Cancel",
			customClass: {
				popup: "alert-modal",
				actions: "btn-container",
				confirmButton: "alert-btn btn-confirm",
				denyButton: "alert-btn btn-cancel",
			},
		}).then((result) => {
			if (result.isConfirmed && !exportMutation.isPending) {
				exportMutation.mutate({
					tableName: "customer",
					filterValues: filterFormik.values,
					companyId: session?.company_id,
				});
			}
		});
	};


	return (
		<div className="customer-list-wrapper">
			<div className="wrapper-row">
				<div className="filter-container">
					<div className="filter-container-inner">
						<div className="filter-items-container">
							<h2 className="filter-section-header">
								Filter Clients
							</h2>
							<p className="action-description">
								The export button downloads all filtered clients
								data to excel file.
							</p>
							<div className="action-row">
								<div
									className="action-btn"
									onClick={handleClientExport}
								>
									<FaFileExport />
									Export
								</div>
								<div
									className="action-btn"
									onClick={() => setDraftCreate(true)}
									// disabled={isEmptyObject(clientFilter)}
								>
									<FaFileCirclePlus />
									Generate draft
								</div>
							</div>
							<FormError
								isError={exportMutation.isError}
								error={exportMutation.error}
								fallback="Failed to export file."
							/>
							<InlineNotice
								message={exportMutation.isSuccess ? "File downloaded" : null}
								type="success"
								autoHideMs={3000}
								onDismiss={() => exportMutation.reset()}
							/>
						</div>
						<div className="filter-items-container">
							<MultipleTagsInput
								label="Customer Tags"
								name="customer_tag"
								onKeyDown={handleKeyDown}
								onBlur={handleTagAdd}
								onTagRemove={handleTagRemove}
								tagsArray={filterFormik.values?.customer_tag}
								autoComplete="off"
							/>
						</div>
						<div className="filter-items-container">
							<div className="input-row">
								<TextField
									name="customer_id"
									label="Customer Id"
									placeholder="e.g. 7 or CUS_0007"
									formik={filterFormik}
								/>
								<TextField
									name="customer_name"
									label="Customer Name"
									placeholder="Enter Customer Name"
									formik={filterFormik}
								/>
							</div>
							<TextField
								name="customer_mobile"
								label="Mobile Number"
								placeholder="Enter Mobile Number"
								formik={filterFormik}
							/>
							<TextField
								name="customer_email"
								label="Customer Email"
								placeholder="Enter Email"
								formik={filterFormik}
							/>
							<div className="input-item">
								<p className="input-label">Customer Gender</p>
								<SelectBox
									name="customer_gender"
									onChange={filterFormik.handleChange}
									onBlur={filterFormik.handleBlur}
									value={filterFormik.values?.customer_gender}
									options={strings.gender}
								/>
								{filterFormik.submitCount > 0 && (
									<div className="validation-error">
										{filterFormik?.errors?.customer_gender}
									</div>
								)}
							</div>
						</div>
						<div className="filter-items-container">
							<div className="input-item">
								<p className="input-label">Customer Type</p>
								<SelectBox
									name="customer_type"
									onChange={filterFormik.handleChange}
									onBlur={filterFormik.handleBlur}
									value={filterFormik.values?.customer_type}
									options={strings.customerType}
								/>
								{filterFormik.submitCount > 0 && (
									<div className="validation-error">
										{filterFormik?.errors?.customer_type}
									</div>
								)}
							</div>
							<TextField
								name="customer_pan_vat"
								label="Customer PAN / VAT"
								placeholder="# Enter Pan / VAT"
								formik={filterFormik}
							/>
						</div>
						<div className="filter-btn-container">
							<div
								className="filter-btn inverted"
								onClick={() => filterFormik.resetForm()}
							>
								<FaEraser />Reset
							</div>
							<div
								className="filter-btn"
								onClick={filterFormik.submitForm}
							>
								<FaMagnifyingGlass />
								Search
							</div>
						</div>
					</div>
				</div>
				<div className="customer-list-container">
					<div className="section-header-container">
						<h2 className="section-header-title">
							Billing Clients
						</h2>
						<div
							className="header-action-btn"
							onClick={() => setAddCustomerView(true)}
						>
							<FaUserPlus />
							Add Customer
						</div>
					</div>
					<FormError
						isError={isError}
						error={error}
						fallback="Failed to load customers."
					/>
					<div className="customer-list-items">
						{!isPending && customers.length === 0 ? (
							<EmptyRecord />
						) : (
							customers.map(({ row, page }) => (
								<UserListItem
									key={row.company_customer_id}
									user={row}
									type="customer"
									currentPageNum={page}
									empPermission={undefined}
								/>
							))
						)}
					</div>
					{(isPending || isFetchingNextPage) && <Spinner styles={undefined} />}
					<div ref={sentinelRef} />
				</div>
			</div>
			{addCustomerView && (
				<CustomerProfile
					isCustomerVisible={addCustomerView}
					onCustomerModalClose={() => setAddCustomerView(false)}
					customer={{ company_customer_id: "" }}
					currentPageNum={undefined}
					onSelectCustomer={(customer, options) => {
						if (options?.openEdit) setCompleteProfileCustomer(customer);
					}}
				/>
			)}
			{completeProfileCustomer && (
				<CustomerProfile
					isCustomerVisible={true}
					onCustomerModalClose={() => setCompleteProfileCustomer(null)}
					customer={completeProfileCustomer}
					currentPageNum={undefined}
					onSelectCustomer={() => {}}
					startEditable
				/>
			)}

			<CustomerBulkDraft
				isOpen={draftCreate}
				onDraftCreateModalClose={() => setDraftCreate(false)}
				filterValues={clientFilter}
			/>
		</div>
	);
}

export default CustomerList;
